import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { t } from '@lingui/macro';
import styled from 'styled-components';
import PlainTextCell from 'components/PlainTextCell';
import spacing from '@decisiv/design-tokens/lib/spacing';

import Tooltip from '@decisiv/ui-components/lib/components/Tooltip';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';
import rem from 'polished/lib/helpers/rem';

import ExclamationTriangle from '@decisiv/iconix/lib/components/ExclamationTriangle';
import Refresh from '@decisiv/iconix/lib/components/Refresh';
import Badge from '@decisiv/ui-components/lib/components/Badge';

import validateApiCredentialsMutation from '../ActionsCell/validateApiCredentialsMutation.graphql';

const StyledBadge = styled(Badge)`
  margin-left: ${rem(spacing.base / 2)};
  height: ${rem(spacing.base * 2)};
  min-height: ${rem(spacing.base * 2)};
  min-width: ${rem(spacing.base * 2)};
  width: ${rem(spacing.base * 2)};
  svg {
    height: ${rem(spacing.base * 1.2)};
    width: ${rem(spacing.base * 1.2)};
  }
` as typeof Badge;

const StyledButton = styled(Button)`
  margin-left: ${rem(spacing.base / 4)};
` as typeof Button;

const InlineWrapper = styled(Flex)`
  display: inline-flex;
  white-space: nowrap;
`;

type Props = {
  rowData: Pick<
    SentinelTeam,
    'id' | 'apiUsername' | 'scheduledForDeletionAt'
  > & {
    apiCredentialsValid?: boolean;
  };
};

export default function ApiUserCell({
  rowData: { id, apiUsername, scheduledForDeletionAt, apiCredentialsValid },
}: Props) {
  const { notify } = useNotifications();

  /* istanbul ignore next -- @preserve */
  const [validateApiCredentials] = useMutation(validateApiCredentialsMutation, {
    onError: (err) =>
      notify(
        {
          id: 'validate-api-credentials-error',
          duration: 5000,
          onClose: () => undefined,
          intent: 'danger',
          title: t`Something Went Wrong`,
        },
        <>{err.message}</>,
      ),
  });

  /* istanbul ignore next -- @preserve */
  const handleOnRefresh = useCallback(async () => {
    try {
      await validateApiCredentials({
        variables: { sentinelTeamId: id },
      });
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  }, [validateApiCredentials, id]);
  const topLine = (
    <InlineWrapper alignItems="center">
      {apiUsername}
      {!apiCredentialsValid && (
        <Flex alignItems="center">
          <Tooltip
            zIndex={100}
            target={
              <StyledBadge
                aria-label={t`Exclamation icon indicating invalid API credentials`}
                color="danger"
                icon={ExclamationTriangle}
                size="medium"
              />
            }
          >
            {t`Invalid API credentials`}
          </Tooltip>
          <Tooltip
            target={
              <StyledButton
                icon={Refresh}
                variant="ghost"
                size="medium"
                aria-label={t`Refresh API credentials for ${id}`}
                onClick={handleOnRefresh}
              />
            }
            zIndex={100}
            placement="top"
          >
            {t`Refresh API Credentials`}
          </Tooltip>
        </Flex>
      )}
    </InlineWrapper>
  );

  return (
    <PlainTextCell
      topLine={topLine}
      color={
        /* istanbul ignore next -- @preserve */
        scheduledForDeletionAt ? 'alaskanHusky' : undefined
      }
    />
  );
}
